// Soumettre un objet au circuit de validation (CV-3) : « qui validera ? » et « qui sera prévenu ? » d'abord (simulation,
// rien n'est ouvert), puis la soumission. L'état « validée » d'une décision vient de l'API (dérivé d'une attestation
// sur l'empreinte courante), jamais de la fiche.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useEkera } from '../contexte';
import { Erreur } from './communs';

const LIBELLE_ETAT_DECISION: Record<string, string> = {
  validee: 'Validée par le circuit de validation',
  validee_version_anterieure: 'Validée sur une version antérieure : la fiche ou le scénario ont changé depuis',
  importee: 'Validation importée (trace d’un autre outil) : elle ne vaut pas validation dans Ekera',
  non_validee: 'Non validée par le circuit',
};

interface Simulee {
  etapes?: { id: string; libelle: string; raisons?: string[]; bloquee?: boolean }[];
  prevenus?: { etape: string; nom?: string | null; sub?: string }[];
}

export function SoumissionDecision({ decision }: { decision: string }) {
  const { client, cid, ecritureAutorisee } = useEkera();
  const qc = useQueryClient();
  const etat = useQuery({ queryKey: ['circuit-objet', cid, decision], queryFn: () => client.etatCircuitObjet(cid, decision), retry: false });
  const [simulee, setSimulee] = useState<Simulee | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  async function faire(f: () => Promise<void>) {
    setErreur(null);
    setEnvoi(true);
    try {
      await f();
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  }
  const e = etat.data?.etat;
  return (
    <section className="panneau pile" aria-labelledby="t-circuit-decision" data-testid="circuit-decision">
      <h2 id="t-circuit-decision">Circuit de validation</h2>
      <p data-testid="etat-circuit-decision">{e ? (LIBELLE_ETAT_DECISION[e] ?? e) : etat.isPending ? 'Lecture de l’état…' : 'État du circuit indisponible.'}</p>
      {ecritureAutorisee && e !== 'validee' && (
        <>
          <div className="ligne">
            <button
              type="button"
              className="btn"
              disabled={envoi}
              onClick={() => void faire(async () => setSimulee((await client.simulationCircuit({ type_objet: 'decision_variante', carte: cid, objet: decision })) as Simulee))}
            >
              Qui validera cette décision ?
            </button>
            <button
              type="button"
              className="btn principal"
              disabled={envoi}
              onClick={() =>
                void faire(async () => {
                  const i = await client.ouvrirInstance({ type_objet: 'decision_variante', carte: cid, objet: decision });
                  setOuverte(i.id);
                  await qc.invalidateQueries({ queryKey: ['circuit-objet', cid, decision] });
                })
              }
            >
              Soumettre au circuit de validation
            </button>
          </div>
          {simulee && (
            <div className="pile" data-testid="qui-validera">
              <ul className="liste">
                {(simulee.etapes ?? []).map((x) => (
                  <li key={x.id}>
                    <b>{x.libelle}</b>
                    {x.bloquee ? ' (bloquée : personne ne tient ce rôle)' : ''} — {(x.raisons ?? []).join(' ; ') || 'toujours'}
                  </li>
                ))}
              </ul>
              <p className="note">
                Seront prévenus : {(simulee.prevenus ?? []).map((x) => x.nom ?? x.sub ?? '?').join(', ') || 'personne pour l’instant'}.
              </p>
            </div>
          )}
        </>
      )}
      {ouverte && (
        <p className="encadre ok" role="status">
          Décision soumise : <a href={`#/circuits/${ouverte}`}>suivre l’instance</a>. Les décideurs se prononcent dans Ekera,
          par un geste sur leur clé ; la fiche sera figée à la fin du circuit.
        </p>
      )}
      {!!erreur && <Erreur erreur={erreur} titre="Rien n’a été soumis." />}
    </section>
  );
}
